import type { PublicState } from '@karozik/shared';
import { Artwork, Avatar, cn, Equalizer, FloatingReactions, useNow, type PartyConnection } from '@karozik/ui';
import { BlindtestScreen } from './BlindtestScreen';
import { useEffect } from 'react';
import { FacesScreen } from './FacesScreen';
import { LyricsPanel } from './LyricsPanel';
import { MomentOverlay } from './MomentOverlay';
import { PhotoWall } from './PhotoWall';
import { usePhotoWall } from './usePhotoWall';
import { fetchLyrics, useLyrics } from './useLyrics';

/**
 * The projector view (16:9). Everything is sized in viewport units so it reads
 * from the back of the room; the phone is only a remote, the show is here.
 */
export function Screen({ conn, position }: { conn: PartyConnection; position: () => number }) {
  const state = conn.state;
  const now = useNow(500) + conn.clockSkew;
  // Never during the games: the lyrics would give the blind test answer away.
  const lyricsOn = !!state?.staff?.showLyrics && !state.blindtest && !state.faces;
  const eventId = state?.event.id ?? '';
  const lyrics = useLyrics(eventId, state?.nowPlaying?.track.id ?? null, lyricsOn);
  const slide = usePhotoWall(state);
  const nextId = state?.queue[0]?.track.id;
  useEffect(() => {
    if (lyricsOn && nextId) void fetchLyrics(eventId, nextId);
  }, [lyricsOn, eventId, nextId]);

  if (!state) return <div className="fixed inset-0 bg-night" />;
  const np = state.nowPlaying;
  const synced = lyrics?.synced ? lyrics : null;
  const cover = synced ? 'h-[min(38vh,22vw)] w-[min(38vh,22vw)]' : 'h-[min(52vh,28vw)] w-[min(52vh,28vw)]';

  return (
    <div className="fixed inset-0 overflow-hidden bg-night text-ink select-none" style={{ cursor: 'none' }}>
      <Backdrop url={np?.track.artworkUrl ?? null} heat={state.heat} />

      <main className="relative grid h-full grid-cols-[1.5fr_1fr] gap-[3vw] px-[3.5vw] pt-[3.5vh] pb-[12vh]">
        <section className="flex min-w-0 flex-col justify-center">
          {np ? (
            <div key={np.id} className="animate-pop flex min-w-0 items-center gap-[2.5vw]">
              <div className="relative shrink-0">
                <Artwork track={np.track} className={cn(cover, 'shadow-[0_2vh_8vh_rgba(0,0,0,0.6)] transition-[width,height] duration-700')} rounded="rounded-[2.5vh]" />
                {!state.paused && (
                  <div className="absolute -bottom-[2vh] left-1/2 flex -translate-x-1/2 items-center gap-[0.6vw] rounded-full bg-night/80 px-[1.2vw] py-[0.8vh] text-[1.6vh] font-bold tracking-widest text-pink uppercase backdrop-blur">
                    <Equalizer className="h-[1.8vh] text-pink" /> En direct
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <h1
                  className={cn(
                    'font-display line-clamp-3 leading-[1.05] font-bold hyphens-auto [overflow-wrap:anywhere]',
                    np.track.title.length > 34 ? 'text-[min(4.2vh,2.6vw)]' : np.track.title.length > 18 ? 'text-[min(5vh,3vw)]' : 'text-[min(6.5vh,3.8vw)]',
                  )}
                >
                  {np.track.title}
                </h1>
                <div className="mt-[1.5vh] truncate text-[3.6vh] text-ink-dim">{np.track.artist}</div>
                {np.track.year && <div className="mt-[1vh] inline-block rounded-full bg-white/10 px-[1vw] py-[0.4vh] text-[2.2vh] text-ink-dim">{np.track.year}</div>}
                {np.proposer ? (
                  <div className="mt-[3vh] inline-flex max-w-full items-center gap-[0.8vw] rounded-full py-[0.8vh] pr-[1.4vw] pl-[0.8vh] text-[2.6vh] whitespace-nowrap" style={{ background: `${np.proposer.color}33` }}>
                    <Avatar guest={np.proposer} size="md" className="!h-[5vh] !w-[5vh] !text-[3vh]" />
                    <span className="truncate">
                      Proposé par <b>{np.proposer.pseudo}</b>
                    </span>
                    {np.votes > 0 && <span className="text-ink-dim">· ❤️ {np.votes}</span>}
                  </div>
                ) : (
                  <div className="mt-[3vh] text-[2.2vh] text-ink-faint">{np.source === 'autopilot' ? '🤖 Choisi par l’autopilote — à vous de jouer !' : ''}</div>
                )}
              </div>
            </div>
          ) : (
            <div className="font-display text-[5vh] text-ink-dim">La musique va commencer…</div>
          )}
          {np && synced && <LyricsPanel key={`l${np.id}`} lyrics={synced} position={position} />}
          {np?.dedication && (
            <div key={`d${np.id}`} className="bg-gradient-neon animate-pop mt-[5vh] self-start rounded-[2vh] px-[2vw] py-[1.8vh] text-[3.2vh] font-semibold shadow-2xl">
              💌 « {np.dedication} »{np.proposer && <span className="font-normal opacity-80"> — {np.proposer.pseudo}</span>}
            </div>
          )}
        </section>

        <aside className="flex min-w-0 flex-col gap-[3vh]">
          <JoinCard state={state} />
          <UpNext state={state} />
        </aside>
      </main>

      <BottomBar state={state} />
      {/* Below the moments and the games, under the reactions flying up. */}
      <PhotoWall slide={slide} state={state} />
      <FloatingReactions reactions={conn.reactions} showNames size="text-[7vh]" />
      <MomentOverlay state={state} now={now} />
      {state.blindtest && <BlindtestScreen state={state} now={now} />}
      {state.faces && <FacesScreen state={state} now={now} clockSkew={conn.clockSkew} />}
      {state.event.status === 'ended' && <EndedScreen state={state} />}
    </div>
  );
}

function Backdrop({ url, heat }: { url: string | null; heat: number }) {
  return (
    <div className="absolute inset-0" aria-hidden>
      {url && <img key={url} src={url} alt="" className="animate-pop absolute inset-0 h-full w-full scale-125 object-cover opacity-30 blur-[8vh]" />}
      <div
        className="animate-spin-slow absolute -top-[30vh] -left-[20vw] h-[90vh] w-[90vh] rounded-full opacity-40 blur-[10vh]"
        style={{ background: 'radial-gradient(circle, #ff3cac, transparent 65%)', animationDuration: `${Math.max(6, 30 - heat / 4)}s` }}
      />
      <div
        className="animate-spin-slow absolute -right-[15vw] -bottom-[35vh] h-[100vh] w-[100vh] rounded-full opacity-30 blur-[10vh]"
        style={{ background: 'radial-gradient(circle, #2bd2ff, transparent 65%)', animationDirection: 'reverse' }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-night/30 via-transparent to-night/80" />
    </div>
  );
}

function JoinCard({ state }: { state: PublicState }) {
  const token = state.event.joinUrl.split('/j/')[1];
  const host = state.event.joinUrl.replace(/^https?:\/\//, '').split('/')[0];
  return (
    <div className="glass flex items-center gap-[1.5vw] rounded-[2.5vh] p-[2vh]">
      <img src={`/api/join/${token}/qr.svg`} alt="" className="h-[22vh] w-[22vh] rounded-[1.5vh] bg-white p-[0.8vh]" />
      <div className="min-w-0">
        <div className="font-display text-[3.4vh] leading-tight font-bold">
          C’est vous
          <br />
          <span className="text-gradient">le DJ !</span>
        </div>
        <div className="mt-[1vh] text-[2vh] text-ink-dim">Scanne, choisis un pseudo, propose tes titres.</div>
        <div className="mt-[0.8vh] truncate text-[1.6vh] text-ink-faint">{host}</div>
        {state.event.wifiSsid && (
          <div className="mt-[1.2vh] flex items-center gap-[0.6vw]">
            <img src={`/api/join/${token}/wifi.svg`} alt="" className="h-[7vh] w-[7vh] rounded-[0.8vh] bg-white p-[0.4vh]" />
            <span className="text-[1.7vh] text-ink-dim">
              Wi-Fi
              <br />
              <b className="text-ink">{state.event.wifiSsid}</b>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function UpNext({ state }: { state: PublicState }) {
  return (
    <div className="min-h-0 flex-1">
      <h2 className="mb-[1.5vh] text-[2vh] font-bold tracking-[0.3em] text-ink-faint uppercase">À suivre</h2>
      <ol className="space-y-[1.4vh]">
        {state.queue.slice(0, 4).map((item, i) => (
          <li key={item.id} className={cn('flex items-center gap-[1vw] rounded-[1.8vh] p-[1vh]', i === 0 ? 'glass' : 'opacity-80')}>
            <Artwork track={item.track} className="h-[8vh] w-[8vh]" rounded="rounded-[1.2vh]" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[2.5vh] font-semibold">{item.track.title}</div>
              <div className="truncate text-[2vh] text-ink-dim">{item.track.artist}</div>
            </div>
            {item.proposer ? (
              <div className="flex flex-col items-center text-[1.5vh] text-ink-dim">
                <Avatar guest={item.proposer} className="!h-[4.5vh] !w-[4.5vh] !text-[2.6vh]" />
                <span className="max-w-[7vw] truncate">{item.proposer.pseudo}</span>
              </div>
            ) : (
              <span className="text-[3vh] opacity-60">🤖</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function BottomBar({ state }: { state: PublicState }) {
  return (
    <footer className="absolute inset-x-0 bottom-0 flex h-[10vh] items-center gap-[2vw] px-[3.5vw]">
      <div className="rounded-full bg-white/10 px-[1.4vw] py-[1vh] text-[2.4vh] font-semibold">
        {state.phase.emoji} {state.phase.name}
      </div>
      <div className="flex flex-1 items-center gap-[1vw]">
        <span className="text-[3.4vh]">🔥</span>
        <div className="h-[1.6vh] flex-1 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full transition-[width] duration-1000"
            style={{ width: `${Math.max(3, state.heat)}%`, background: 'linear-gradient(90deg, #ffd23f, #ff3cac 60%, #ff5c7a)' }}
          />
        </div>
        <span className="w-[10vw] text-[2vh] text-ink-dim">{heatLabel(state.heat)}</span>
      </div>
      <div className="text-[2.2vh] text-ink-dim">👥 {state.guestsOnline} DJ connectés</div>
    </footer>
  );
}

function heatLabel(h: number): string {
  if (h > 80) return 'Le feu ! 🔥🔥';
  if (h > 55) return 'Ça chauffe !';
  if (h > 25) return 'Ça se réveille';
  return 'Envoyez des 🔥';
}

function EndedScreen({ state }: { state: PublicState }) {
  return (
    <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-night/90 text-center backdrop-blur">
      <div className="text-[14vh]">💜</div>
      <h1 className="font-display text-[8vh] font-bold text-gradient">Merci à tous !</h1>
      <p className="mt-[2vh] text-[3vh] text-ink-dim">Retrouvez la playlist et les trophées de la soirée sur vos téléphones.</p>
      <p className="mt-[1vh] text-[2.4vh] text-ink-faint">{state.event.name}</p>
    </div>
  );
}
